/**
 * API Client
 *
 * HTTP client configurado para funcionar com Python/FastAPI.
 * Usa JWT para autenticação com refresh automático.
 *
 * @since 2025-12-15
 * @updated 2025-12-18 - Adicionado refresh automático do token
 * @epic INIT-0045-03, INIT-0046
 */

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { getBackendUrl, getApiPrefix, endpoints } from '@/config/backend';
import i18n from '@/i18n/config';

/**
 * Converte o locale do i18next (ex: "pt-BR", "en-US") para o formato
 * canônico de Accept-Language usado pelo backend.
 *
 * Mantém o hífen no header (HTTP convention) e o backend converte para
 * id_i18n via resolve_i18n_id (aceita pt_br, pt-br e variantes).
 */
function buildAcceptLanguageHeader(): string | null {
  const lang = (i18n.language || '').trim();
  if (!lang) return null;
  const primary = lang.split('-')[0]?.toLowerCase();
  return primary && primary !== lang.toLowerCase() ? `${lang},${primary};q=0.9` : lang;
}

// ==========================================
// TOKEN MANAGEMENT
// ==========================================

/**
 * Valida se uma string é um JWT válido (formato correto).
 * JWT deve ter 3 partes separadas por ponto: header.payload.signature
 */
function isValidJwtFormat(token: string | null): boolean {
  if (!token || typeof token !== 'string' || token.trim() === '') {
    return false;
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }

  // Verifica se cada parte não está vazia
  if (parts.some((part) => !part || part.trim() === '')) {
    return false;
  }

  // Tenta decodificar o payload para garantir que é um JSON válido
  try {
    const payload = JSON.parse(atob(parts[1]));
    // JWT deve ter pelo menos 'exp' ou 'iat'
    return typeof payload === 'object' && payload !== null && ('exp' in payload || 'iat' in payload);
  } catch {
    return false;
  }
}

/**
 * Verifica se o token JWT está expirado.
 */
function isTokenExpired(token: string): boolean {
  if (!isValidJwtFormat(token)) {
    return true;
  }
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) {
      return false; // Sem exp, assume válido
    }
    const exp = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= exp;
  } catch {
    return true;
  }
}

/**
 * Dispara evento de sessão expirada para redirecionar ao login.
 */
function triggerSessionExpired(): void {
  console.warn('[Auth] Sessão expirada, redirecionando para login...');
  window.dispatchEvent(new CustomEvent('auth:session-expired'));
}

// Funcao para obter JWT token (retorna null se token for inválido/expirado)
function getJwtToken(): string | null {
  const token = localStorage.getItem('tmf_token');

  // Token não existe
  if (token === null) {
    return null;
  }

  // Token malformado
  if (!isValidJwtFormat(token)) {
    console.warn('[Auth] Token inválido encontrado no storage, limpando...');
    localStorage.removeItem('tmf_token');
    localStorage.removeItem('tmf_refresh_token');
    triggerSessionExpired();
    return null;
  }

  // Token expirado
  if (isTokenExpired(token)) {
    console.warn('[Auth] Token expirado encontrado no storage');
    // Não limpa aqui - deixa o interceptor tentar refresh primeiro
    // Se o refresh falhar, o interceptor vai limpar e disparar evento
  }

  return token;
}

// Funcao para obter refresh token (retorna null se token for inválido)
function getRefreshToken(): string | null {
  const token = localStorage.getItem('tmf_refresh_token');

  if (token === null) {
    return null;
  }

  if (!isValidJwtFormat(token)) {
    console.warn('[Auth] Refresh token inválido encontrado no storage, limpando...');
    localStorage.removeItem('tmf_refresh_token');
    return null;
  }

  // Refresh token também pode expirar
  if (isTokenExpired(token)) {
    console.warn('[Auth] Refresh token expirado');
    localStorage.removeItem('tmf_refresh_token');
    triggerSessionExpired();
    return null;
  }

  return token;
}

// Funcao para salvar tokens (valida antes de salvar)
function saveTokens(accessToken: string, refreshToken: string): void {
  if (!isValidJwtFormat(accessToken)) {
    console.error('[Auth] Tentativa de salvar access token inválido ignorada');
    return;
  }
  if (!isValidJwtFormat(refreshToken)) {
    console.error('[Auth] Tentativa de salvar refresh token inválido ignorada');
    return;
  }
  localStorage.setItem('tmf_token', accessToken);
  localStorage.setItem('tmf_refresh_token', refreshToken);
}

// Funcao para limpar tokens
function clearTokens(): void {
  localStorage.removeItem('tmf_token');
  localStorage.removeItem('tmf_refresh_token');
  localStorage.removeItem('tmf_user');
}

// Funcao para obter user ID do storage (para header X-User-ID)
function getUserIdFromStorage(): number | null {
  try {
    const authStorage = localStorage.getItem('tmf-auth-storage');
    if (authStorage) {
      const parsed = JSON.parse(authStorage);
      return parsed?.state?.user?.id ?? null;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

// Verifica se o token está próximo de expirar (5 minutos antes)
function isTokenExpiringSoon(token: string): boolean {
  // Primeiro valida o formato do token
  if (!isValidJwtFormat(token)) {
    return true; // Token inválido deve ser tratado como expirado
  }

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    return exp - now < fiveMinutes;
  } catch {
    return true; // Erro no parse = token expirado/inválido
  }
}

// ==========================================
// REFRESH TOKEN LOGIC
// ==========================================

// Estado do refresh - evita múltiplas tentativas simultâneas
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

// Adiciona requisição à fila de espera
function subscribeTokenRefresh(callback: (token: string) => void): void {
  refreshSubscribers.push(callback);
}

// Notifica todas as requisições em espera com o novo token
function onTokenRefreshed(token: string): void {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

// Tenta fazer refresh do token
async function refreshTokens(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    // Usa axios diretamente para evitar loops
    // withCredentials: true para enviar cookie httpOnly do refresh token
    const response = await axios.post(
      `${getBackendUrl()}${getApiPrefix()}${endpoints.auth.refresh()}`,
      { refresh_token: refreshToken },
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        withCredentials: true,
      }
    );

    const { access_token, refresh_token } = response.data;
    if (access_token && refresh_token) {
      saveTokens(access_token, refresh_token);
      if (import.meta.env.DEV) console.debug('[Auth] Token refreshed successfully');
      return access_token;
    }

    return null;
  } catch (error) {
    console.error('[Auth] Token refresh failed:', error);
    clearTokens();
    return null;
  }
}

// ==========================================
// AXIOS CLIENT
// ==========================================

// Create axios instance
const createApiClient = (baseURLOverride?: string): AxiosInstance => {
  const client = axios.create({
    baseURL: baseURLOverride ?? `${getBackendUrl()}${getApiPrefix()}`,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    timeout: 30000,
    withCredentials: true, // Cookies httpOnly para JWT (com fallback header Authorization)
  });

  // Request interceptor
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // Skip auth for login/refresh endpoints
      const isAuthEndpoint =
        config.url?.includes('/auth/login') ||
        config.url?.includes('/auth/refresh') ||
        config.url?.includes('/public/');

      if (!isAuthEndpoint) {
        let token = getJwtToken();

        // Verifica se o token está próximo de expirar e faz refresh proativo.
        // Usa a mesma trava isRefreshing do caminho 401 para evitar race condition:
        // N requisições concorrentes resultam em apenas 1 chamada a /auth/refresh.
        // Fix: TMF9-56982 (#2/#3)
        if (token && isTokenExpiringSoon(token)) {
          if (isRefreshing) {
            // Já há um refresh em voo — aguarda o resultado em vez de disparar outro
            token = await new Promise<string | null>((resolve) => {
              subscribeTokenRefresh((newToken: string) => resolve(newToken || null));
            });
          } else {
            isRefreshing = true;
            if (import.meta.env.DEV) console.debug('[Auth] Token expiring soon, refreshing proactively...');
            try {
              const newToken = await refreshTokens();
              if (newToken) {
                onTokenRefreshed(newToken);
                token = newToken;
              } else {
                onTokenRefreshed('');
              }
            } finally {
              isRefreshing = false;
            }
          }
        }

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      // Enviar X-User-ID para filtragem de permissões
      const userId = getUserIdFromStorage();
      if (userId) {
        config.headers['X-User-ID'] = String(userId);
      }

      // i18n: enviar Accept-Language com base no locale ativo (TMF9-53649)
      // Backend usa para resolver title via resource_item_i18n.
      // Não sobrescreve se o caller já definiu o header explicitamente.
      if (!config.headers['Accept-Language']) {
        const acceptLanguage = buildAcceptLanguageHeader();
        if (acceptLanguage) {
          config.headers['Accept-Language'] = acceptLanguage;
        }
      }

      // Para FormData (uploads), remover Content-Type para que o browser
      // defina automaticamente com o boundary correto
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  client.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      // Handle 401 - Unauthorized
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        // Skip retry for auth endpoints
        const isAuthEndpoint =
          originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/refresh');

        if (isAuthEndpoint) {
          clearTokens();
          return Promise.reject(error);
        }

        // Marca para evitar loop infinito
        originalRequest._retry = true;

        if (isRefreshing) {
          // Se já está fazendo refresh, espera o resultado
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh((token: string) => {
              if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(client(originalRequest));
              } else {
                reject(error);
              }
            });
          });
        }

        isRefreshing = true;

        try {
          const newToken = await refreshTokens();

          if (newToken) {
            // Notifica outras requisições em espera
            onTokenRefreshed(newToken);
            isRefreshing = false;

            // Re-tenta a requisição original com o novo token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return client(originalRequest);
          } else {
            // Refresh falhou - redireciona para login
            clearTokens();
            isRefreshing = false;
            refreshSubscribers = [];

            // Dispara evento para o AuthContext tratar
            window.dispatchEvent(new CustomEvent('auth:session-expired'));
            return Promise.reject(error);
          }
        } catch (refreshError) {
          clearTokens();
          isRefreshing = false;
          refreshSubscribers = [];

          window.dispatchEvent(new CustomEvent('auth:session-expired'));
          return Promise.reject(refreshError);
        }
      }

      // Handle 403 - Forbidden (reportar via auto-report)
      if (error.response?.status === 403) {
        console.error('Acesso negado:', error.response.data);
        const url = originalRequest?.url || 'unknown';
        const method = (originalRequest?.method || 'GET').toUpperCase();
        const errorMsg = (error.response?.data as Record<string, unknown>)?.message
          || (error.response?.data as Record<string, unknown>)?.detail
          || 'Acesso negado';
        window.dispatchEvent(new CustomEvent('api:server-error', {
          detail: {
            status: 403,
            url,
            method,
            message: `API 403 Forbidden: ${method} ${url} - ${errorMsg}`,
          },
        }));
      }

      // Handle 404 - Not Found (reportar via auto-report, exceto rotas esperadas/não-implementadas)
      if (error.response?.status === 404) {
        const url = originalRequest?.url || 'unknown';
        const method = (originalRequest?.method || 'GET').toUpperCase();
        // Prefixos de módulos cujo backend ainda não está implementado ou 404 é esperado
        const ignoredPrefixes = [
          '/content/contents/code/',
          // [TMF9-54249] FragmentEditorModal usa 404 como sinal de "create mode"
          '/content/fragments/by-code/',
          '/resource/resources/code/',
          '/campaign/',
          '/budget/',
          '/store/',
          '/functions/',
          '/quiz/',
          '/import/',
          '/integrations/lambda/',
          '/ranking/',
          '/report/',
          '/communication/',
          '/ai/',
          '/automation/',
        ];
        const isIgnored = ignoredPrefixes.some((prefix) => url.includes(prefix));
        if (!isIgnored) {
          const errorMsg = (error.response?.data as Record<string, unknown>)?.message
            || (error.response?.data as Record<string, unknown>)?.detail
            || 'Recurso não encontrado';
          window.dispatchEvent(new CustomEvent('api:server-error', {
            detail: {
              status: 404,
              url,
              method,
              message: `API 404 Not Found: ${method} ${url} - ${errorMsg}`,
            },
          }));
        }
      }

      // Handle 500 - Internal Server Error
      if (error.response?.status === 500) {
        const url = originalRequest?.url || 'unknown';
        const method = (originalRequest?.method || 'GET').toUpperCase();
        const errorMsg = (error.response?.data as Record<string, unknown>)?.message
          || (error.response?.data as Record<string, unknown>)?.error
          || 'Internal Server Error';
        window.dispatchEvent(new CustomEvent('api:server-error', {
          detail: {
            status: 500,
            url,
            method,
            message: `API 500: ${method} ${url} - ${errorMsg}`,
          },
        }));
      }

      // Handle 422 - Validation Error (FastAPI)
      if (error.response?.status === 422) {
        console.error('Erro de validacao:', error.response.data);
      }

      // Handle 502/503 - Backend indisponível (silenciar para módulos não-implementados)
      if (error.response?.status === 502 || error.response?.status === 503) {
        const url = originalRequest?.url || 'unknown';
        const method = (originalRequest?.method || 'GET').toUpperCase();
        const ignoredPrefixes = [
          '/campaign/', '/budget/', '/store/', '/functions/',
          '/quiz/', '/import/', '/integrations/lambda/',
          '/ranking/', '/report/', '/communication/', '/ai/',
          '/automation/',
        ];
        const isIgnored = ignoredPrefixes.some((prefix) => url.includes(prefix));
        if (!isIgnored) {
          window.dispatchEvent(new CustomEvent('api:server-error', {
            detail: {
              status: error.response.status,
              url,
              method,
              message: `API ${error.response.status}: ${method} ${url} - Service Unavailable`,
            },
          }));
        }
      }

      // Handle network errors
      if (!error.response) {
        console.error('Erro de rede:', error.message);
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// Instancia singleton
let apiClientInstance: AxiosInstance | null = null;
let sdApiClientInstance: AxiosInstance | null = null;

export function getApiClient(): AxiosInstance {
  if (!apiClientInstance) {
    apiClientInstance = createApiClient();
  }
  return apiClientInstance;
}

/**
 * Instância dedicada ao motor local do Service Desk (tmf_service_desk_service,
 * TMF9-57379). Base distinta de getApiClient(): desde a TMF9-64217 o backend
 * monta o `router` agregado da lib (`tmf-service-desk-back`) SEM prefixo
 * adicional — os routers de domínio já declaram seus próprios paths
 * absolutos (`/api/tickets`, `/api/kb/search`, `/api/service/...` etc.), então
 * a base real é apenas `/api`, sem o `/v1` do restante da aplicação e sem o
 * `/service-desk` que existia antes do release TMF9-64215.
 * Achado ao vivo em 2026-07-10: o adapter usava a base genérica (/api/v1) e
 * todo o CRUD de tickets retornava 404 silencioso (mascarado como "sem tickets").
 * TMF9-64219: removida a duplicação `/api/service-desk/api` que a montagem
 * antiga exigia — ver `tmf9SDApiClient.ts` para o mapeamento path a path.
 */
export function getSdApiClient(): AxiosInstance {
  if (!sdApiClientInstance) {
    sdApiClientInstance = createApiClient(`${getBackendUrl()}/api`);
  }
  return sdApiClientInstance;
}

// Para casos onde precisa recriar o client (ex: mudanca de backend)
export function resetApiClient(): void {
  apiClientInstance = null;
  sdApiClientInstance = null;
}

// API helper methods
const apiClient = getApiClient();

// Named export for compatibility with { apiClient } imports
export { apiClient };

export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    getApiClient()
      .get<T>(url, config)
      .then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    getApiClient()
      .post<T>(url, data, config)
      .then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    getApiClient()
      .put<T>(url, data, config)
      .then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    getApiClient()
      .patch<T>(url, data, config)
      .then((res) => res.data),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    getApiClient()
      .delete<T>(url, config)
      .then((res) => res.data),
};

export const sdApi = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    getSdApiClient()
      .get<T>(url, config)
      .then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    getSdApiClient()
      .post<T>(url, data, config)
      .then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    getSdApiClient()
      .put<T>(url, data, config)
      .then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    getSdApiClient()
      .patch<T>(url, data, config)
      .then((res) => res.data),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    getSdApiClient()
      .delete<T>(url, config)
      .then((res) => res.data),
};

// Exportar funções de token para uso em outros módulos
/**
 * Header Authorization com o token da sessão (tmf_token), ou {} sem sessão.
 * Para fetch cru fora do apiClient: as chaves 'token'/'auth_token' do
 * localStorage nunca são gravadas e mandavam "Bearer null" [TMF9-64736].
 */
function authHeader(): Record<string, string> {
  const token = getJwtToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export { isValidJwtFormat, isTokenExpired, saveTokens, clearTokens, getJwtToken, getRefreshToken, triggerSessionExpired, authHeader };

export default apiClient;
